import Stepper from "../../../designComponents/Stepper";
import { ReactNode, useEffect, useState } from "react";
import Address from "./Address";
import BasicInformation from "./BasicInformation";
import Amenities from "./Amenities";
import Photo from "./Photo";
import Description from "./Description";
import { useNavigate, useParams } from "react-router-dom";
import requestAPI from "../../../utils/requestAPI";
import properties from "../../../apiConfigs/properties";
import { PropertyAddressType } from "./types";
import { toast } from "react-toastify";

export type AddPropertyProps = {
  initialValues: PropertyAddressType;
  onUpdate: (data: any) => void;
  gotoPrevious: () => void;
  errorMessage: string;
  loading: boolean;
  setLoading: (loading: boolean) => void;
};

const componentSteps = [
  Address,
  BasicInformation,
  Amenities,
  Photo,
  Description,
];

const UpdateProperty = () => {
  const navigate = useNavigate();
  const { propertyId } = useParams<{ propertyId: string }>();
  const [currentStep, setCurrentStep] = useState(1);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [initialValues, setInitialValues] = useState<PropertyAddressType>({
    address: "",
    bathrooms: 0,
    bedrooms: 0,
    area: 0,
    images: [],
    features: [],
    rentalMarket: "",
    propertyType: "",
    furnishedType: "",
    price: 0,
    berRating: "",
  });

  const stepNames = [
    "Address",
    "Basic Information",
    "Amenities",
    "Photo",
    "Description",
  ];
  const NUMBER_OF_STEPS = stepNames.length;

  const getPropertyDetail = async (id: string) => {
    try {
      const response = await requestAPI(properties.getSingleProperty(id));
      setInitialValues({
        ...response,
        price: response?.pricing?.price.replace(/\D/g, ""),
      });
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (propertyId) {
      getPropertyDetail(propertyId);
    }
  }, [propertyId]);

  const goToNextStep = () => {
    setCurrentStep((prev) => (prev < NUMBER_OF_STEPS ? prev + 1 : prev));
    if (currentStep === NUMBER_OF_STEPS) {
      toast.success("property updated successfully");
      navigate("/properties");
    }
  };
  const gotoPreviousStep = () =>
    setCurrentStep((prev) => (prev > 0 ? prev - 1 : prev));

  const makeFinalData = (
    stepData: PropertyAddressType,
    step: number
  ): (PropertyAddressType & { updateStep: number }) | undefined => {
    switch (step) {
      case 1:
        return {
          address: stepData.address,
          updateStep: 1,
        };
      case 2:
        return {
          bathrooms: stepData.bathrooms,
          bedrooms: stepData.bedrooms,
          area: stepData.area,
          rentalMarket: stepData.rentalMarket,
          furnishedType: stepData.furnishedType,
          propertyType: stepData.propertyType,
          price: stepData.price,
          berRating: stepData.berRating,
          updateStep: 2,
        };
      case 3:
        return {
          features: stepData.features,
          updateStep: 3,
        };
      case 4:
        return {
          images: stepData.images,
          updateStep: 4,
        };
      case 5:
        return {
          description: stepData.description,
          updateStep: 5,
        };
      default:
        return undefined;
    }
  };
  const handleUpdateStep = async (stepData: PropertyAddressType) => {
    if (!propertyId) {
      console.error("Property ID is undefined");
      return;
    }
    setInitialValues((prev) => ({ ...prev, ...stepData }));
    setLoading(true);
    try {
      const finalData = makeFinalData(stepData, currentStep);
      await requestAPI(properties.updateProperty(propertyId, finalData));
      goToNextStep();

    } catch (e: any) {
      setErrorMessage(e.message);
    } finally {
      setLoading(false);
    }
  };

  const renderStepComponent = () => {
    return componentSteps.reduce<Record<number, ReactNode>>(
      (acc, Component, index) => {
        acc[index + 1] = (
          <Component
            gotoPrevious={gotoPreviousStep}
            initialValues={initialValues}
            onUpdate={handleUpdateStep}
            key={index}
            errorMessage={errorMessage}
            loading={loading}
            setLoading={setLoading}
          />
        );
        return acc;
      },
      {}
    )[currentStep];
  };

  return (
    <>
      <div style={{ display: "flex", gap: "30px", marginBottom: "30px" }}>
        <Stepper
          currentStep={currentStep}
          numberOfSteps={NUMBER_OF_STEPS}
          stepNames={stepNames}
        />

        <div
          style={{ display: "flex", flexDirection: "column", width: "100%" }}
        >
          <div style={{ marginBottom: "30px" }}>{renderStepComponent()}</div>
        </div>
      </div>
    </>
  );
};

export default UpdateProperty;
